import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Star } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Thumbnail } from './Thumbnail';
import { SelectionCheckbox } from './SelectionCheckbox';
import { ItemActionMenu } from './ItemActionMenu';
import { PreviewToggle } from './PreviewToggle';
import { useItemListContext } from './ItemListProvider';
import { useItemActions } from '@/hooks/useItemActions';
import type { NormalizedItem } from './types';

interface FileRowProps {
  item: NormalizedItem;
  variant?: 'full' | 'minimal';
  parentId?: string;
  onClickItem?: (item: NormalizedItem) => void;
  /** Project files: show the eye toggle for the project's main view (editors only) */
  showPreviewToggle?: boolean;
  /** Extra controls at the end of the row (e.g. download on the share-link page) */
  actions?: React.ReactNode;
}

export function FileRow({ item, variant = 'full', parentId, onClickItem, showPreviewToggle = false, actions }: FileRowProps) {
  const { isSelected, updateIsQuerying, onRefresh, permissions, sortCriteria, sortAscending } = useItemListContext();
  const { isFavorite } = useItemActions({ updateIsQuerying, onRefresh });
  const params = useParams();
  const selected = isSelected(item.id);
  const favorite = isFavorite(item.id);

  const parentType = parentId ? 'folder' : 'project';
  const pid = parentId || params.id;
  // Use the current route's viewType so prev/next navigation includes all files in the same view
  const viewType = params.viewType || 'allfiles';
  const sortQuery = `?sortBy=${sortCriteria}&ascending=${sortAscending}`;
  const linkTo = pid
    ? `/in/${parentType}/${pid}/resource/${item.id}/${viewType}${sortQuery}`
    : undefined;

  const canTogglePreview = variant === 'full' && showPreviewToggle && permissions.canEdit && item.isRendable;

  const formattedDate = item.created
    ? format(new Date(item.created), 'dd.MM.yyyy')
    : '';
  const formattedSize = item.size ? formatFileSize(Number(item.size)) : '';

  const content = (
    <div
      className={cn(
        'group flex items-center gap-3 rounded-xl border px-3 py-2 transition-all',
        selected ? 'border-primary bg-primary-muted' : 'border-transparent bg-card hover:bg-secondary hover:shadow-sm',
      )}
    >
      {variant === 'full' && <SelectionCheckbox item={item} />}
      <Thumbnail item={item} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{item.title || item.name}</p>
        <p className="text-xs text-muted-foreground">
          {formattedDate}
          {formattedSize && ` · ${formattedSize}`}
        </p>
      </div>
      {favorite && <Star className="h-3.5 w-3.5 shrink-0 fill-yellow-400 text-yellow-400" />}
      {canTogglePreview && <PreviewToggle item={item} />}
      {actions}
      {variant === 'full' && <ItemActionMenu item={item} />}
    </div>
  );

  if (variant === 'minimal' && onClickItem) {
    return (
      <li className="cursor-pointer" onClick={() => onClickItem(item)}>
        {content}
      </li>
    );
  }

  if (linkTo) {
    return (
      <li>
        <Link to={linkTo} className="block no-underline">
          {content}
        </Link>
      </li>
    );
  }

  return <li>{content}</li>;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
